import React, { useContext, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import { leagueCustomerMembershipLifetimeRoute } from "network/api-routes";
import { lifetimeToggleOverride } from "./member-since";

const LifetimeToggleButton = ({ customerId, membership, onChanged }) => {
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [pendingOverride, setPendingOverride] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const lifetime = membership?.lifetime;
  if (!membership || !lifetime) return null;

  const toggleOverride = lifetimeToggleOverride(lifetime);
  const confirmKey = {
    granted: "gestion.accountFinance.lifetimeGrantConfirm",
    revoked: "gestion.accountFinance.lifetimeRevokeConfirm",
    automatic: "gestion.accountFinance.lifetimeAutomaticConfirm",
  }[pendingOverride ?? "automatic"];

  const submit = () => {
    setSaving(true);
    performRequest(
      "PATCH",
      leagueCustomerMembershipLifetimeRoute(customerId, membership.id),
      { override: pendingOverride },
      true,
      user,
    )
      .then(() => {
        setPendingOverride(undefined);
        onChanged();
      })
      .catch((error) =>
        enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
          variant: "error",
        }),
      )
      .finally(() => setSaving(false));
  };

  return (
    <>
      <Stack component="span" direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="small" onClick={() => setPendingOverride(toggleOverride)}>
          {intl.formatMessage({
            id: toggleOverride === "granted" ? "gestion.accountFinance.lifetimeGrant" : "gestion.accountFinance.lifetimeRevoke",
          })}
        </Button>
        {lifetime.override && (
          <Button size="small" color="inherit" onClick={() => setPendingOverride(null)}>
            {intl.formatMessage({ id: "gestion.accountFinance.lifetimeAutomatic" })}
          </Button>
        )}
      </Stack>
      <Dialog open={pendingOverride !== undefined} onClose={() => setPendingOverride(undefined)} maxWidth="xs" fullWidth>
        <DialogTitle>{intl.formatMessage({ id: "gestion.accountFinance.lifetimeTitle" })}</DialogTitle>
        <DialogContent>
          <RevaText variant="body2">{intl.formatMessage({ id: confirmKey })}</RevaText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingOverride(undefined)} disabled={saving}>
            {intl.formatMessage({ id: "gestion.accountFinance.cancel" })}
          </Button>
          <Button variant="contained" onClick={submit} disabled={saving}>
            {intl.formatMessage({ id: "gestion.accountFinance.lifetimeConfirm" })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LifetimeToggleButton;
